import { Alert, Button, Chip, Link, Paper, Stack, Typography } from '@mui/material'
import { useState } from 'preact/hooks'
import type { Dashboard } from '../api/generated/Dashboard'
import type { LiveShift } from '../api/generated/LiveShift'
import type { Withdrawal } from '../api/generated/Withdrawal'
import { useApi } from '../api/useApi'
import { volunteerName } from '../assignments/status'
import { formatSentAt } from '../conversations/format'
import { TimezoneNote } from '../shifts/TimezoneNote'
import { formatRange } from '../shifts/time'
import { liveCounts, missingMessage } from './counts'
import { ReplacementDialog } from './ReplacementDialog'
import { useLiveReload } from './useLiveReload'

type Replaced = Pick<LiveShift, 'id' | 'position_name' | 'starts_at' | 'ends_at'>

/** Volunteers who withdrew from shifts still to fill, most recent first. */
function Withdrawals({
  slug,
  timezone,
  withdrawals,
  onReplace,
}: {
  slug: string
  timezone: string
  withdrawals: Withdrawal[]
  onReplace: (shift: Replaced) => void
}) {
  return (
    <>
      <Typography variant="subtitle1">Désistements</Typography>
      {withdrawals.map((withdrawal) => (
        <Paper key={withdrawal.assignment_id} variant="outlined" sx={{ p: 1.5 }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Link
              href={`/events/${slug}/positions/${withdrawal.position_id}`}
              sx={{ fontWeight: 'medium' }}
            >
              {withdrawal.position_name}
            </Link>
            <Typography variant="body2" color="text.secondary">
              {formatRange(withdrawal.starts_at, withdrawal.ends_at)}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Désistement de {volunteerName(withdrawal)},{' '}
            {formatSentAt(withdrawal.withdrawn_at, timezone)}
          </Typography>
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'center', mt: 1 }}
          >
            <Chip
              size="small"
              color="warning"
              label={`${withdrawal.accepted_volunteers}/${withdrawal.required_volunteers} validés`}
            />
            <Button
              size="small"
              onClick={() => onReplace({ ...withdrawal, id: withdrawal.shift_id })}
            >
              Remplacer
            </Button>
          </Stack>
        </Paper>
      ))}
    </>
  )
}

/** Organizers' live view of the shifts in progress or about to start, alerts first, then withdrawals; hidden when none. */
export function LiveBoard({ slug, timezone }: { slug: string; timezone: string }) {
  const { data, error, reload } = useApi<Dashboard>(`/events/${slug}/dashboard`)
  useLiveReload(slug, reload)
  const [replacing, setReplacing] = useState<Replaced>()
  const shifts = data?.shifts
  const withdrawals = data?.withdrawals
  if (!error && !shifts?.length && !withdrawals?.length) return null

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Typography variant="h6">En direct</Typography>
        <TimezoneNote timezone={timezone} />
        {error && <Alert severity="error">{error}</Alert>}
        {shifts?.map((shift) => (
          <Paper
            key={shift.id}
            variant="outlined"
            sx={{ p: 1.5, borderColor: shift.alert ? 'error.main' : undefined }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'baseline',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Link
                href={`/events/${slug}/positions/${shift.position_id}`}
                sx={{ fontWeight: 'medium' }}
              >
                {shift.position_name}
              </Link>
              <Typography variant="body2" color="text.secondary">
                {formatRange(shift.starts_at, shift.ends_at)}
                {!shift.started && ' · à venir'}
              </Typography>
            </Stack>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {liveCounts(shift).map(({ label, color }) => (
                <Chip key={label} size="small" label={label} color={color} />
              ))}
            </Stack>
            {shift.alert && (
              <Alert
                severity="error"
                sx={{ mt: 1 }}
                action={
                  <Button color="inherit" size="small" onClick={() => setReplacing(shift)}>
                    Remplacer
                  </Button>
                }
              >
                {missingMessage(shift)}
              </Alert>
            )}
          </Paper>
        ))}
        {withdrawals && withdrawals.length > 0 && (
          <Withdrawals
            slug={slug}
            timezone={timezone}
            withdrawals={withdrawals}
            onReplace={setReplacing}
          />
        )}
        {replacing && (
          <ReplacementDialog
            slug={slug}
            shift={replacing}
            onClose={() => setReplacing(undefined)}
          />
        )}
      </Stack>
    </Paper>
  )
}
